import * as React from 'react';
import {FunctionComponent, useState} from 'react';
import {observer} from 'mobx-react-lite';
import {PartialWorkout} from '../../../Models/PartialWorkout';
import {PresenterProps, withPresenter} from '../../../Support/with_presenter';
import {WorkoutsTablePresenter} from './WorkoutsTablePresenter';
import {WorkoutDateRangePicker} from '../../Components/Form/WorkoutDateRangePicker';
import {classNames} from '../../Support/classNames';
import {WorkoutList} from '../../Components/Workout/WorkoutList';
import {useObserveSearchParams} from '../../Support/useObserveSearchParams';
import {PartialWorkoutsSummary} from '../../Components/Workout/PartialWorkoutsSummary';
import {Feedback} from '../../Components/Feedback';
import {UploadForm} from './UploadForm';
import {deserialise, serialise, WorkoutsTableFilter} from './WorkoutsTableFilter';
import {AdvancedAdvert} from '../../Components/AdvancedAdvert';
import {FeatureFlags} from '../../../FeatureFlags';

interface OwnProps {
    partialWorkouts: PartialWorkout[];
    initialPartialFilter: Partial<WorkoutsTableFilter> | null | undefined;
}

const WorkoutsTableComponent: FunctionComponent<OwnProps & PresenterProps<WorkoutsTablePresenter>> = observer(
    ({presenter}) => {
        useObserveSearchParams<WorkoutsTableFilter | null>(
            presenter.filter,
            (filter) => presenter.onPartialFilterChange(filter),
            serialise,
            deserialise,
        );
        const [showExtraFilterOptions, setExtraFilterOptions] = useState(
            !!(presenter.filter.dateRange || presenter.filter.distanceRange),
        );

        return (
            <div className="row">
                <div className="col-12 col-md-9">
                    <div className="row mb-2">
                        <div className="col-sm-5 d-flex flex-row gap-3 mb-2">
                            <WorkoutDateRangePicker
                                className="flex-grow-1"
                                range={presenter.filter?.dateRange}
                                onChange={(range) => presenter.onRangeChange(range)}
                            />
                        </div>
                        <div className="col-sm-7 d-flex flex-row gap-3 mb-2">
                            <input
                                type="text"
                                className="form-control"
                                placeholder="Filter by workout title"
                                value={presenter.filter?.title || ''}
                                onChange={(e) => presenter.onTitleFilterChange(e.target.value)}
                            />
                            <div className="form-check mt-2 form-switch">
                                <input
                                    className="form-check-input"
                                    type="checkbox"
                                    role="switch"
                                    checked={showExtraFilterOptions}
                                    onChange={() => setExtraFilterOptions((v) => !v)}
                                />
                                <label className="form-check-label" htmlFor="extendedModeCheckbox">
                                    More
                                </label>
                            </div>
                            <button className="btn btn-primary" onClick={() => presenter.resetFilter()}>
                                Reset
                            </button>
                        </div>
                    </div>
                    {showExtraFilterOptions && (
                        <div className="row mb-3">
                            <div className="col-sm-6 mb-3">
                                <div className="input-group has-validation">
                                    <span className="input-group-text">Distance (km)</span>
                                    <input
                                        type="number"
                                        className="form-control"
                                        placeholder="Min"
                                        value={presenter.filter?.distanceRange?.min?.kilometers ?? ''}
                                        onChange={(e) => presenter.onMinDistanceChange(e.target.value)}
                                    />
                                    <input
                                        type="number"
                                        className="form-control"
                                        placeholder="Max"
                                        value={presenter.filter?.distanceRange?.max?.kilometers ?? ''}
                                        onChange={(e) => presenter.onMaxDistanceChange(e.target.value)}
                                    />
                                    <div className="invalid-feedback"></div>
                                </div>
                            </div>
                            <div className="col-sm-6 mb-3">
                                <div className="input-group has-validation">
                                    <span className="input-group-text">Duration (min)</span>
                                    <input
                                        type="number"
                                        className="form-control"
                                        placeholder="Min"
                                        value={presenter.filter?.durationRange?.min?.minutes ?? ''}
                                        onChange={(e) => presenter.onMinDurationChange(e.target.value)}
                                    />
                                    <input
                                        type="number"
                                        className="form-control"
                                        placeholder="Max"
                                        value={presenter.filter?.durationRange?.max?.minutes ?? ''}
                                        onChange={(e) => presenter.onMaxDurationChange(e.target.value)}
                                    />
                                    <div className="invalid-feedback"></div>
                                </div>
                            </div>
                        </div>
                    )}
                    <div className="row">
                        <div className="col-12">
                            <div className="row">
                                <div className="col-xs-12 col-md-6">
                                    <h3 className="mb-2">
                                        {presenter.numWorkouts === 1
                                            ? '1 Workout'
                                            : presenter.numWorkouts + ' Workouts'}
                                    </h3>
                                </div>
                                <div className="col-xs-12 col-md-6 gap-2 d-flex justify-content-start justify-content-md-end mb-2">
                                    {presenter.selectedWorkoutIds.length > 0 && (
                                        <>
                                            <button
                                                className="btn btn-sm btn-danger float-end"
                                                onClick={() => {
                                                    if (
                                                        confirm(
                                                            'Are you sure you want to delete the selected workouts?',
                                                        )
                                                    ) {
                                                        presenter.deleteSelectedWorkouts();
                                                    }
                                                }}
                                            >
                                                <i className="bi-trash me-2" />
                                                {presenter.selectedWorkoutIds.length === 1 ? (
                                                    <>Delete workout</>
                                                ) : (
                                                    <>Delete {presenter.selectedWorkoutIds.length} workouts</>
                                                )}
                                            </button>
                                            <button
                                                className="btn btn-sm btn-secondary"
                                                onClick={() => presenter.clearSelectedWorkouts()}
                                            >
                                                <i className="bi-x-lg me-2" />
                                                Clear
                                            </button>
                                        </>
                                    )}
                                </div>
                            </div>

                            <div className="card mb-3">
                                <WorkoutList
                                    workoutListViewModels={presenter.workoutListViewModels}
                                    selectable
                                    selectedWorkoutIds={presenter.selectedWorkoutIds}
                                    onSelectionChanged={(workoutIds) => presenter.onSelectionChanged(workoutIds)}
                                />
                            </div>
                            <div className="row">
                                <div className="col-12 d-flex justify-content-center">
                                    <ul className="pagination">
                                        <li className="page-item">
                                            <button
                                                className={classNames('page-link', {
                                                    disabled: presenter.filter.page === 1,
                                                })}
                                                onClick={() => presenter.previousPage()}
                                                disabled={presenter.filter.page === 1}
                                            >
                                                Previous
                                            </button>
                                        </li>
                                        {presenter.pages.map((page) => {
                                            return (
                                                <li
                                                    key={page}
                                                    className={classNames('page-item', {
                                                        active: page === presenter.filter.page,
                                                    })}
                                                >
                                                    <button
                                                        className="page-link"
                                                        onClick={() => presenter.onPageChange(page)}
                                                    >
                                                        {page}
                                                    </button>
                                                </li>
                                            );
                                        })}
                                        <li className="page-item">
                                            <button
                                                className={classNames('page-link', {
                                                    disabled: presenter.filter.page === presenter.numPages,
                                                })}
                                                onClick={() => presenter.nextPage()}
                                                disabled={presenter.filter.page === presenter.numPages}
                                            >
                                                Next
                                            </button>
                                        </li>
                                    </ul>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
                <div className="col-12 col-md-3">
                    <PartialWorkoutsSummary
                        className="mb-3 d-none d-md-flex"
                        partialWorkouts={presenter.filteredPartialWorkouts}
                        dateRange={presenter.filter?.dateRange || undefined}
                    />
                    <UploadForm className="mb-3" />
                    {FeatureFlags.isAdvancedSubscriptionsEnabled && !FeatureFlags.isAdvanced && (
                        <AdvancedAdvert className="mb-3" />
                    )}
                    <Feedback className="d-none d-md-flex" />
                </div>
            </div>
        );
    },
);

export const WorkoutsTable = withPresenter<WorkoutsTablePresenter, OwnProps>(
    (props, component) =>
        new WorkoutsTablePresenter(props.initialPartialFilter, props.partialWorkouts, component.network.workouts),
    WorkoutsTableComponent,
);
